import React, { FC, ReactNode, useEffect, useState, useMemo } from 'react'
import { Dropdown, Checkbox, Select, Input } from 'antd'
import AntdTooltip from 'UI/Tooltip/Tooltip'
import styles from './Filters.module.scss'
import { CloseOutlined } from '@ant-design/icons'
import { IFilterOptions } from '../useFilterOptions'
import { mapStrArrToOpts, mapStrArrToOptsTitle, mapStrArrToTokenTypeOpts, formatFilterValues } from './utils'
import Button from 'UI/Button/Button'

const filterIconUrl = require('./icons/filter.svg')
const filterBlueIconUrl = require('./icons/filterBlue.svg')

export interface ISubmitParams extends IFilterOptions {
  gainLossType: string
  gainLossValue: string
}
interface IFilters {
  options: IFilterOptions
  children?: ReactNode
  onSubmit: (params: ISubmitParams | null) => void
}

const Filters: FC<IFilters> = ({ options, children, onSubmit }) => {
  const [visible, setVisible] = useState<boolean>(false)

  const [selectedActivitiesFrom, setSelectedActivitiesFrom] = useState<string[]>([])
  const [selectedActivityTypes, setSelectedActivityTypes] = useState<string[]>([])
  const [selectedDirections, setSelectedDirections] = useState<string[]>([])
  const [selectedTokenTypes, setSelectedTokenTypes] = useState<string[]>([])
  const [selectedGainLoss, setSelectedGainLoss] = useState<string>('greaterThan')
  const [gainLossValue, setGainLossValue] = useState<string>('')

  const resetValues = () => {
    setSelectedActivitiesFrom([])
    setSelectedActivityTypes([])
    setSelectedDirections([])
    setSelectedTokenTypes([])
    setSelectedGainLoss('greaterThan')
    setGainLossValue('')
  }

  const filterSelected = useMemo(() => {
    return (
      selectedActivitiesFrom.length > 0 ||
      selectedActivityTypes.length > 0 ||
      selectedDirections.length > 0 ||
      selectedTokenTypes.length > 0 ||
      gainLossValue !== ''
    )
  }, [selectedActivitiesFrom, selectedActivityTypes, selectedDirections, selectedTokenTypes, gainLossValue])

  useEffect(() => {
    if (!children) {
      resetValues()
      setVisible(false)
    }
  }, [children])

  const onApply = () => {
    onSubmit({
      activitiesFrom: selectedActivitiesFrom,
      activityTypes: selectedActivityTypes,
      directions: selectedDirections,
      tokenTypes: selectedTokenTypes,
      gainLossType: selectedGainLoss,
      gainLossValue: gainLossValue,
    })
    setVisible(false)
  }

  const onReset = () => {
    resetValues()
    setVisible(false)
    onSubmit(null)
  }

  return (
    <div className={styles.container}>
      {children}
      <Dropdown
        trigger={['click']}
        open={visible}
        rootClassName={styles.root}
        onOpenChange={open => setVisible(open)}
        dropdownRender={() => (
          <div className={styles.popup}>
            <div className={styles.header}>
              <span>Filters</span>
              <button className={styles.close} onClick={() => setVisible(false)}>
                <CloseOutlined />
              </button>
            </div>
            <div className={styles.content}>
              <div className={styles.item}>
                <div className={styles.label}>Revenue Sources</div>
                <Select
                  mode='multiple'
                  allowClear
                  style={{ flex: 1 }}
                  placeholder='Select'
                  onChange={(value: string[]) => {
                    setSelectedActivitiesFrom(value)
                  }}
                  options={mapStrArrToOpts(options.activitiesFrom)}
                  menuItemSelectedIcon={({ isSelected }) => <Checkbox checked={isSelected} />}
                  popupClassName={styles.selectPopup}
                  value={selectedActivitiesFrom}
                />
              </div>
              <div className={styles.item}>
                <div className={styles.label}>Activity Type</div>
                <Select
                  mode='multiple'
                  allowClear
                  style={{ flex: 1 }}
                  placeholder='Select'
                  onChange={(value: string[]) => {
                    setSelectedActivityTypes(value)
                  }}
                  options={mapStrArrToOpts(options.activityTypes)}
                  menuItemSelectedIcon={({ isSelected }) => <Checkbox checked={isSelected} />}
                  popupClassName={styles.selectPopup}
                  value={selectedActivityTypes}
                />
              </div>
              <div className={styles.item}>
                <div className={styles.label}>Direction</div>
                <Select
                  mode='multiple'
                  allowClear
                  style={{ flex: 1 }}
                  placeholder='Select'
                  onChange={(value: string[]) => {
                    setSelectedDirections(value)
                  }}
                  options={mapStrArrToOptsTitle(options.directions)}
                  menuItemSelectedIcon={({ isSelected }) => <Checkbox checked={isSelected} />}
                  popupClassName={styles.selectPopup}
                  value={selectedDirections}
                />
              </div>
              <div className={styles.item}>
                <div className={styles.label}>Token Type</div>
                <Select
                  mode='multiple'
                  allowClear
                  style={{ flex: 1 }}
                  placeholder='Select'
                  onChange={(value: string[]) => {
                    setSelectedTokenTypes(value)
                  }}
                  options={mapStrArrToTokenTypeOpts(options.tokenTypes)}
                  menuItemSelectedIcon={({ isSelected }) => <Checkbox checked={isSelected} />}
                  popupClassName={styles.selectPopup}
                  value={selectedTokenTypes}
                />
              </div>
              <div className={styles.item}>
                <div className={styles.label}>Gain Loss</div>
                <Select
                  style={{ flex: 2.5 }}
                  placeholder='Select'
                  onChange={(value: string) => {
                    setSelectedGainLoss(value)
                  }}
                  options={[
                    { value: 'greaterThan', label: 'Greater than' },
                    { value: 'lessThan', label: 'Less than' },
                  ]}
                  popupClassName={styles.selectPopup}
                  value={selectedGainLoss}
                />
                <Input
                  style={{ flex: 1 }}
                  prefix='$'
                  value={gainLossValue}
                  onChange={e => setGainLossValue(e.target.value)}
                />
              </div>
            </div>
            <div className={styles.footer}>
              <Button buttonType='secondary' onClick={onReset}>
                Reset
              </Button>
              <Button buttonType='primary' onClick={onApply}>
                Apply
              </Button>
            </div>
          </div>
        )}
      >
        <div className={styles.btnContainer}>
          <div
            onClick={() => {
              if (visible === false) setVisible(true)
            }}
          >
            <AntdTooltip
              title={
                filterSelected && children ? (
                  <div className={styles.filtersWrapper}>
                    Filters:
                    <ul>
                      {selectedActivitiesFrom.length > 0 && (
                        <li>Revenue Sources: {formatFilterValues(selectedActivitiesFrom)}</li>
                      )}
                      {selectedActivityTypes.length > 0 && (
                        <li>Activity Types: {formatFilterValues(selectedActivityTypes)}</li>
                      )}
                      {selectedDirections.length > 0 && <li>Directions: {formatFilterValues(selectedDirections)}</li>}
                      {selectedTokenTypes.length > 0 && <li>Token Types: {formatFilterValues(selectedTokenTypes)}</li>}
                      {gainLossValue !== '' && (
                        <li>
                          Gain Loss: {selectedGainLoss === 'greaterThan' ? 'Greater than' : 'Less than'} $
                          {gainLossValue}
                        </li>
                      )}
                    </ul>
                  </div>
                ) : (
                  'Filter'
                )
              }
              placement='top'
              color='#383838'
              classNames={{ root: styles.tooltipRoot, body: styles.tooltipBody }}
              mainTextContentClassName={styles.tooltipMainTextContent}
            >
              {(visible && filterSelected) || Boolean(children) ? (
                <img src={filterBlueIconUrl} alt='filter' className={styles.imageFilter} />
              ) : (
                <img src={filterIconUrl} alt='filter' className={styles.imageFilter} />
              )}
            </AntdTooltip>
          </div>
        </div>
      </Dropdown>
    </div>
  )
}

export default Filters
